import { Link, useOutletContext } from "react-router-dom";
import { Activity as ActivityIcon, Film, KeyRound, ListVideo, MonitorCheck, MonitorOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useApi } from "@/lib/api";
import { fmtDate, fmtRelative, SUB_LABELS } from "@/lib/format";
import type { Activity, Screen } from "@/lib/types";
import { Loading, PageHeader } from "@/components/app/PageHeader";
import { StatusPill, screenState } from "@/components/app/StatusPill";

interface Dash {
  organization: { name: string; plan: string; screen_quota: number; subscription_status: string };
  screens_total: number; screens_online: number; screens_offline: number; screens_revoked: number;
  media_count: number; playlists_count: number; groups_count: number;
  screens: Screen[]; recent_activity: Activity[];
}

function Metric({ label, value, icon: Icon, tone, testId }: { label: string; value: number; icon: any; tone: string; testId: string }) {
  return (
    <div className="panel p-5">
      <div className="flex items-center justify-between"><p className="eyebrow">{label}</p><Icon className={`h-4 w-4 ${tone}`} /></div>
      <p data-testid={testId} className={`mt-3 font-display text-4xl font-semibold ${tone}`}>{value}</p>
    </div>
  );
}

export default function DashboardPage() {
  const { data, loading } = useApi<Dash>("/dashboard");
  const { openPairing } = useOutletContext<{ openPairing: () => void }>();
  if (loading && !data) return <Loading />;
  if (!data) return null;
  const quotaPct = data.organization.screen_quota ? (data.screens_total / data.organization.screen_quota) * 100 : 100;

  return (
    <div>
      <PageHeader eyebrow={data.organization.name} title="Tableau de bord" description="État de votre parc d'écrans et derniers événements." />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label="Écrans en ligne" value={data.screens_online} icon={MonitorCheck} tone="text-emerald-400" testId="metric-screens-online-count" />
        <Metric label="Écrans hors ligne" value={data.screens_offline} icon={MonitorOff} tone="text-amber-400" testId="metric-screens-offline-count" />
        <Metric label="Médias actifs" value={data.media_count} icon={Film} tone="text-blue-400" testId="metric-media-count" />
        <Metric label="Listes de lecture" value={data.playlists_count} icon={ListVideo} tone="text-cyan-400" testId="metric-playlists-count" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="panel p-6">
          <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-medium text-slate-100">Écrans</h2><Link to="/ecrans" data-testid="dashboard-link-screens" className="text-sm text-blue-400 hover:text-blue-300">Tout voir</Link></div>
          {data.screens.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">Aucun écran jumelé pour l'instant.</p>
          ) : (
            <div className="divide-y divide-slate-800">
              {data.screens.map((s) => (
                <Link key={s.id} to={`/ecrans/${s.id}`} data-testid={`dashboard-screen-${s.id}`} className="flex items-center justify-between py-3 transition-colors hover:bg-slate-800/30">
                  <div><p className="text-sm font-medium text-slate-200">{s.name}</p><p className="text-xs text-slate-500">{s.group_name || "Sans groupe"} · vu {fmtRelative(s.last_seen_at).toLowerCase()}</p></div>
                  <StatusPill state={screenState(s)} />
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="panel p-6">
            <p className="eyebrow">Abonnement</p>
            <div className="mt-2 flex items-baseline justify-between"><p className="font-display text-xl text-slate-100">Forfait {data.organization.plan}</p><span className="text-xs text-slate-400">{SUB_LABELS[data.organization.subscription_status]}</span></div>
            <div className="mt-4 flex justify-between text-xs text-slate-400"><span>Quota d'écrans</span><span data-testid="dashboard-quota">{data.screens_total} / {data.organization.screen_quota}</span></div>
            <Progress value={quotaPct} className="mt-2 h-2 bg-slate-800" />
          </div>
          <div className="panel border-cyan-500/20 p-6">
            <KeyRound className="h-5 w-5 text-cyan-400" />
            <h3 className="mt-3 text-lg font-medium text-slate-100">Nouvel écran ?</h3>
            <p className="mt-1 text-sm text-slate-400">Lancez l'application sur la TV, puis saisissez le code à 6 chiffres affiché.</p>
            <Button data-testid="dashboard-btn-pair" onClick={openPairing} className="mt-4 w-full bg-blue-600 text-white hover:bg-blue-500">Saisir un code de jumelage</Button>
          </div>
        </div>
      </div>

      <div className="panel mt-6 p-6">
        <div className="mb-4 flex items-center justify-between"><h2 className="flex items-center gap-2 text-lg font-medium text-slate-100"><ActivityIcon className="h-4 w-4 text-cyan-400" />Activité récente</h2><Link to="/historique" className="text-sm text-blue-400 hover:text-blue-300">Historique complet</Link></div>
        <ol className="space-y-3" data-testid="dashboard-activity-feed">
          {data.recent_activity.map((a) => (
            <li key={a.id} className="flex gap-3 text-sm"><span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-cyan-500/70" /><div><p className="text-slate-200">{a.message}</p><p className="text-xs text-slate-500">{fmtDate(a.created_at)}{a.actor_email ? ` · ${a.actor_email}` : " · appareil"}</p></div></li>
          ))}
          {data.recent_activity.length === 0 && <p className="text-sm text-slate-500">Aucun événement.</p>}
        </ol>
      </div>
    </div>
  );
}
